import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../environments";
import {NativeSharedStorage} from '../../native/shared';
import { tap, catchError, delay, map } from "rxjs/operators";
import { withErrorHandler } from './error-handler.util';
import { of, throwError } from "rxjs";

export interface GetStatsResponse {
  played: any[];
  unplayed: any[];
}

export function isPlayedStat(entry: { played?: unknown } | null | undefined): boolean {
  if (entry?.played === true) {
    return true;
  }

  if (entry?.played === false || entry?.played == null) {
    return false;
  }

  return Number(entry.played) === 1;
}

export function readGetStatsResponse(response: unknown): GetStatsResponse {
  if (Array.isArray(response)) {
    return {
      played: response.filter((entry) => isPlayedStat(entry)),
      unplayed: response.filter((entry) => !isPlayedStat(entry)),
    };
  }

  if (!response || typeof response !== "object") {
    return { played: [], unplayed: [] };
  }

  const payload = response as { played?: unknown; unplayed?: unknown };
  return {
    played: Array.isArray(payload.played) ? payload.played : [],
    unplayed: Array.isArray(payload.unplayed) ? payload.unplayed : [],
  };
}

@Injectable({ providedIn: "root" })
export class StatsService {
  private readonly baseURL = environment.API_URL + "stats";

  constructor(private http: HttpClient) {}

  private serializeTournamentData(tournament_data?: any): string | null {
    if (tournament_data == null) {
      return null;
    }

    if (typeof tournament_data === "string") {
      return tournament_data;
    }

    return JSON.stringify(tournament_data);
  }

  getStats(
    mode: "" | "all" | "master" | "all-master",
    fromDate: Date,
    toDate: Date,
    buyInMin: number,
    buyInMax: number,
    site: any[],
    game: any[],
    speed: any[],
    type: any[],
    players: any[],
    variant: any[],
    users: any[],
    usePlayTime: boolean = false,
    name?: string | null
  ) {
    const normalizedName = typeof name === "string" ? name.trim() : "";
    const params: { [key: string]: any } = {
      from_date: fromDate ? fromDate.toISOString() : undefined,
      to_date: toDate ? toDate.toISOString() : undefined,
      buy_in_min: buyInMin ? buyInMin.toString() : undefined,
      buy_in_max: buyInMax ? buyInMax.toString() : undefined,
      site: site?.length > 0 ? site?.join(",") : undefined,
      game: game?.length > 0 ? game?.join(",") : undefined,
      speed: speed?.length > 0 ? speed?.join(",") : undefined,
      type: type?.length > 0 ? type?.join(",") : undefined,
      players: players?.length > 0 ? players?.join(",") : undefined,
      variant: variant?.length > 0 ? variant?.join(",") : undefined,
      users: users?.length > 0 ? users : undefined,
      name: normalizedName || undefined,
      use_play_time: usePlayTime,
      mode,
    };

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    );

    return this.http.get<GetStatsResponse>(this.baseURL + "/get-stats", {
      params,
    }).pipe(
      withErrorHandler({
        operation: 'getStats',
        cache: true,
        fallback: { played: [], unplayed: [] },
        timeoutMs: 60000,
        retries: 1
      }),
      map((response) => readGetStatsResponse(response))
    );
  }

  getStatForSchedule(schedule: number, date: string) {
    return this.http.get<any>(
      this.baseURL + "/get-stat-for-schedule/" + schedule,
      {
        params: {
          date: date,
        },
      }
    ).pipe(
      withErrorHandler({
        operation: 'getStatForSchedule',
        cache: true,
        fallback: {}
      })
    );
  }

  getStatsForDay(date: string, order?: { [key: string]: number }, userId?: number | null) {
    const url = userId
      ? `${this.baseURL}/get-stats-for-day/${date}/${userId}`
      : `${this.baseURL}/get-stats-for-day/${date}`;
    return this.http.get<any[]>(url, {
      params: {
        order: order
          ? Object.entries(order)
              .map((e) => e.join(":"))
              .join(",")
          : undefined,
      },
    }).pipe(
      withErrorHandler({
        operation: 'getStatsForDay',
        cache: true,
        fallback: []
      })
    );
  }

  getPlaySessionList(page: number = 0) {
    return this.http.get<any>(`${this.baseURL}/play-session/list`, {
      params: {
        page: String(page),
      },
    }).pipe(
      withErrorHandler({
        operation: 'getPlaySessionList',
        cache: false,
        fallback: []
      })
    );
  }

  getCurrentPlaySession() {
    return this.http.get<any>(`${this.baseURL}/play-session/current`).pipe(
      withErrorHandler({
        operation: 'getCurrentPlaySession',
        cache: false,
        fallback: null
      })
    );
  }

  updatePlaySession(payload: {
    session?: number | null;
    start: string | null;
    end: string | null;
    force_create?: boolean;
  }) {
    return this.http.put<any>(`${this.baseURL}/play-session/update`, payload).pipe(
      withErrorHandler({
        operation: 'updatePlaySession',
        cache: false,
        fallback: null
      })
    );
  }

  updateStats(payload: {
    id?: number | string | null;
    played?: boolean;
    time?: string;
    play_time?: string | null;
    rebuy?: number | null;
    itm?: number | null;
    add_on?: number | null;
    bounty?: number | null;
    end_pos?: number | null;
    custom_buy_in?: number | null;
    tournament_data?: any;
    auto_bankroll?: boolean;
  }) {
    const hasIdKey = Object.prototype.hasOwnProperty.call(payload, "id");
    const hasIdValue = payload.id !== null && payload.id !== undefined && payload.id !== "";
    const parsedId = hasIdValue ? Number(payload.id) : NaN;
    const safeId = hasIdValue && Number.isFinite(parsedId) ? parsedId : null;

    const hasTournamentDataKey = Object.prototype.hasOwnProperty.call(payload, "tournament_data");
    const serializedTournamentData = this.serializeTournamentData(payload.tournament_data);

    const requestPayload: { [key: string]: any } = {
      ...payload,
    };

    if (hasIdKey) {
      requestPayload.id = safeId;
    }

    if (hasTournamentDataKey) {
      requestPayload.tournament_data = serializedTournamentData;
    }

    Object.keys(requestPayload).forEach(
      (key) => requestPayload[key] === undefined && delete requestPayload[key]
    );

    return this.http.post(`${this.baseURL}/update-stats`, requestPayload).pipe(
      withErrorHandler({ operation: 'updateStats', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('updateStats failed:', err);
        return throwError(() => err);
      }),
      tap(() => {
        localStorage.setItem("refresh-schedules", "true");

        let ids: number[] = [];
        try {
          const stored = NativeSharedStorage.getItem("playingIds");
          if (stored) {
            ids = JSON.parse(stored);
            if (!Array.isArray(ids)) ids = [];
          }
        } catch (e) {
          ids = [];
        }

        const played = requestPayload.played;
        const tid = hasIdKey ? requestPayload.id : null;

        if (played && tid != null) {
          if (!ids.includes(tid)) {
            ids.push(tid);
          }
        } else if (tid != null) {
          ids = ids.filter(id => id !== tid);
        }

        NativeSharedStorage.setItem("playingIds", JSON.stringify(ids));
      })
    );
  }
}
